import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingAccent, marketingButton, marketingType } from "../../marketing.type";
import {
  marketingAudienceCtasData,
  type AudienceCta,
  type AudienceKey,
} from "./marketingAudienceCtas.data";

export type Props = {
  /** Which audiences to offer. All three, in the copy's order, by default. */
  audiences?: AudienceKey[];
  /** Called with the chosen pathway, for both tracking and navigation */
  onCtaClick: (cta: AudienceCta) => void;
  /**
   * The two shapes the design draws: the foot of a dark panel, centred with
   * only the form's button; or a pale card on a dark band, centred with the
   * audience label and every route in.
   */
  variant?: "panel" | "card";
  className?: string;
};

/**
 * The ways in, repeated beside whichever section argues to that audience, with
 * the same words the hero's cards use because the same data draws both.
 */
export function MarketingAudienceCtas({
  audiences,
  onCtaClick,
  variant = "panel",
  className,
}: Props) {
  const isCard = variant === "card";
  const shown = (
    audiences
      ? marketingAudienceCtasData.filter((a) => audiences.includes(a.key))
      : marketingAudienceCtasData
  ).map((audience) => ({
    ...audience,
    // A panel argues in one line where a card argues in two, so it takes its
    // own heading where it has one and shows nothing beneath it.
    heading: isCard ? audience.heading : audience.panelHeading ?? audience.heading,
    // This card has the width of the page, so it may argue at more length
    // than the hero's three in a row.
    description: audience.sectionDescription ?? audience.description,
  }));

  return (
    <div className={className} data-testid="marketing-audience-ctas">
      <div className="flex flex-col gap-10">
        {shown.map((audience) => (
          <div
            key={audience.key}
            className={`flex flex-col items-center text-center ${
              isCard
                ? "gap-6 lg:gap-8 bg-brand-50 text-brand-800 rounded-[26px] p-6"
                : "gap-4"
            }`}
          >
            {isCard && (
              <Type
                variant="caption"
                as="p"
                className={`${marketingType.cardEyebrow} ${marketingAccent} max-w-none`}
              >
                {audience.title}
              </Type>
            )}
            {audience.heading && (
              <Type
                variant="h3"
                as="h3"
                className={
                  isCard
                    ? `${marketingType.display} ${marketingType.displayWeight} -mb-2 lg:-my-[9px]`
                    // A sentence rather than a phrase, set a step down from a
                    // card's heading so that it holds one line here.
                    : "text-[20px] leading-[25px] md:text-[20px] lg:text-[24px] md:leading-[25px] lg:leading-[32px] font-bold md:font-bold lg:font-medium"
                }
              >
                {audience.heading}
              </Type>
            )}
            {isCard && audience.description && (
              <Type variant="body2" className={`max-w-none ${marketingType.small}`}>
                {audience.description}
              </Type>
            )}
            <div
              // On a tablet the card's two buttons share a row, and the panel's one
              // button is a fixed pill in the middle rather than full width.
              className={`flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:flex-wrap lg:justify-center lg:gap-3 ${
                isCard ? "md:flex-row" : "pt-2 md:items-center lg:pt-4"
              }`}
            >
              {audience.ctas
                // Only the doing-button, in either shape: the quieter route
                // lives on the hero's card, as the design draws it.
                .filter((cta) => cta.formSlug)
                .map((cta) => (
                  <Button
                    key={cta.label}
                    as="a"
                    href={cta.href}
                    theme={isCard ? "light" : "dark"}
                    variant={cta.variant ?? "primary"}
                    className={`${marketingButton.className} ${
                      isCard
                        ? "h-9 md:flex-1 md:min-w-0 lg:flex-none lg:h-11"
                        : "border-brand-700 h-[41px] md:w-[216px] lg:w-auto"
                    }`}
                    typeClassName={
                      isCard
                        ? marketingButton.compactTypeClassName
                        : marketingButton.typeClassName
                    }
                    data-testid={`marketing-cta-${cta.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      e.preventDefault();
                      onCtaClick(cta);
                    }}
                  >
                    {cta.label}
                  </Button>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MarketingAudienceCtas;
